import { forwardRef, Injectable } from '@angular/core';
import { DefaultSessionsServiceConfigProvider } from '../../config/sessions.config';
import { LocalStorageProvider } from './storage-providers/local-storage.provider';

/**
 * Interface representing HTTP headers and cookies for API access
 */
export interface AccessCredentials {
  /**
   * Map of HTTP headers for authentication
   */
  httpHeaders?: { [key: string]: string };
  /**
   * Map of cookies for authentication
   */
  cookies?: { [key: string]: string };
}

export interface SessionClaims {
  id: string;
  username?: string;
  email?: string;
  imageUri?: string;
  [key: string]: any;
}

/**
 * Represents an active authentication session
 */
export class Session {
  /**
   * Creates a new session instance
   * @param id Unique session identifier
   * @param authProvider Name of the authentication provider
   * @param authCredentials Credentials used to authenticate
   * @param refreshCredentials Credentials for session refresh
   * @param prefillInfo Saved credentials for pre-filling forms
   * @param accessCredentials HTTP headers/cookies for API access
   * @param startedAt Timestamp when session was created
   * @param claims Claims from authentication provider
   * @param expiresAt Optional timestamp when session expires
   * @param refreshedAt Optional timestamp when session was last refreshed
   * @param terminatedAt Optional timestamp when session was terminated
   */
  public constructor(
    public id: string,
    public authProvider: string,
    public authCredentials: GenericCredentials | null,
    public refreshCredentials: GenericCredentials | null,
    public prefillInfo: GenericCredentials | null,
    public accessCredentials: AccessCredentials,
    public startedAt: number,
    public claims: SessionClaims,
    public expiresAt: number | null = null,
    public refreshedAt: number | null = null,
    public terminatedAt: number | null = null
  ) {}

  /**
   * Checks if session has expired based on current time
   * @returns True if session is expired, false otherwise
   */
  public isExpired(): boolean {
    if (this.expiresAt) {
      return this.expiresAt <= new Date().getTime();
    }

    return false;
  }
}

/**
 * Interface defining form fields for authentication providers
 */
export type AuthProviderForm = {
  /**
   * Unique key for the form field
   */
  key: string;
  /**
   * Human-readable label for the form field
   */
  label: string;
  /**
   * HTML input type (text, email, password, checkbox)
   */
  type: 'text' | 'email' | 'password' | 'checkbox';
  /**
   * Whether to save this field for pre-filling
   */
  saveForPrefill: boolean;
  /**
   * Optional icon for the form field
   */
  icon?: string;
}[];

/**
 * Interface defining an authentication provider implementation
 */
export interface AuthProvider {
  /**
   * Unique name of the authentication provider
   */
  name: string;
  /**
   * Human-readable label for the provider
   */
  label: string;
  /**
   * Icon associated with the provider
   */
  icon: string;
  /**
   * Gets the sign-in form fields for this provider
   * @returns Array of form field definitions
   */
  getSignInFields(): AuthProviderForm;
  /**
   * Starts a new authentication session
   * @param credentials User-provided credentials
   * @returns Promise resolving to the created session
   * @throws Error if authentication fails
   */
  startSession(credentials: GenericCredentials): Promise<Session>;
  /**
   * Refreshes an existing session
   * @param session Session to refresh
   * @returns Promise resolving to the updated session
   * @throws Error if refresh fails
   */
  refreshSession(session: Session): Promise<Session>;
  /**
   * Whether this provider supports password reset (defaults to true if not set)
   */
  supportsPasswordReset?: boolean;
  /**
   * Initiates a password reset for the given username
   * @param username The user's email/username
   */
  resetPassword?(username: string): Promise<void>;
  /**
   * Confirms a password reset with the code and new password
   * @param username The user's email/username
   * @param confirmationCode The code sent to the user
   * @param newPassword The new password to set
   */
  confirmResetPassword?(username: string, confirmationCode: string, newPassword: string): Promise<void>;
}

/**
 * Type representing generic key-value credential pairs
 */
export type GenericCredentials = { [key: string]: string };

/**
 * Enum for session service signals
 */
export enum SessionServiceSignal {
  /**
   * Indicates that sign-in is required
   */
  SIGNIN_REQUIRED,
}

/**
 * Abstract class defining a storage provider interface
 */
@Injectable({ providedIn: 'root', useClass: forwardRef(() => LocalStorageProvider) })
export abstract class StorageProvider {
  /**
   * Stores a key-value pair in storage
   * @param key The key to store
   * @param value The value to store
   */
  abstract set(key: string, value: string): void;
  /**
   * Retrieves a value from storage by key
   * @param key The key to look up
   * @returns The stored value or null if not found
   */
  abstract get(key: string): string | null;
  /**
   * Removes a key-value pair from storage
   * @param key The key to remove
   * @returns The removed value or null if not found
   */
  abstract clear(key: string): string | null;
}

/**
 * Abstract class for providing configuration to the sessions service
 */
@Injectable({
  providedIn: 'root',
  useClass: forwardRef(() => DefaultSessionsServiceConfigProvider), // Default implementation.
})
export abstract class SessionsServiceConfigProvider {
  /**
   * Gets the configuration for the sessions service
   * @returns The configuration object
   */
  abstract getConfig(): SessionsServiceConfig;
}

/**
 * Interface defining configuration options for the sessions service
 */
export interface SessionsServiceConfig {
  /**
   * Callback when a session is activated
   * @param session The activated session
   */
  onSessionActivated?: (_: Session) => void;
  /**
   * Array of authentication providers
   */
  authProviders?: AuthProvider[];
  /**
   * Function to determine session identifier
   * @param session Session to identify
   * @returns Unique identifier string
   */
  sessionDiscriminator?: (session: Session) => string;
  /**
   * Whether to persist sessions in local storage
   */
  persistSessions?: boolean;
  /**
   * Whether to automatically refresh credentials
   */
  autoRefreshCredentials?: boolean;
  /**
   * Callback when session selection is required
   */
  onSelectionRequired?: () => void;
  /**
   * Callback when sign-in is required
   * @param session Session requiring sign-in
   */
  onSignInRequired?: (session: Session) => void;
}

/**
 * Service for managing authentication sessions
 */
@Injectable({ providedIn: 'root' })
export class SessionsService {
  private authProviders: AuthProvider[];
  sessionDiscriminator: (session: Session) => string;
  persistSessions: boolean | undefined;
  autoRefreshCredentials: boolean;
  activeSessionId: string | null = null;
  sessions: Session[] = [];

  onSelectionRequired: () => void;
  onSignInRequired: (session: Session) => void;
  onSessionActivated: (session: Session) => void;

  /**
   * Sets the active session and ensures it's valid
   * @param sessionId ID of the session to activate
   * @returns Promise resolving when session is activated
   * @throws Error if session is expired or not found
   */
  public async setActiveSession(sessionId: string): Promise<void> {
    const session = this.getSession(sessionId);
    this.activeSessionId = sessionId;

    if (session.isExpired()) {
      try {
        await this.refreshSession(sessionId);
      } catch {
        this.onSignInRequired(session);
        throw new Error('User action required');
      }
    }

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    this.onSessionActivated(session);
  }

  /**
   * Gets the currently active session
   * @returns Promise resolving to the active session
   * @throws Error if no active session or session is expired
   */
  public async getActiveSession(): Promise<Session> {
    if (!this.activeSessionId) {
      this.onSelectionRequired();
      throw new Error('No active session');
    }

    const session = this.getSession(this.activeSessionId);
    if (session.isExpired()) {
      try {
        await this.refreshSession(this.activeSessionId);
      } catch {
        this.onSignInRequired(session);
        throw new Error('Session expired');
      }
    }

    return session;
  }

  /**
   * Gets all stored sessions
   * @returns Copy of the sessions array
   */
  public getSessions(): Session[] {
    return [...this.sessions];
  }

  /**
   * Gets available authentication providers
   * @returns Copy of the providers array
   */
  public getAuthenticationProviders(): AuthProvider[] {
    return [...this.authProviders];
  }

  /**
   * Sets session claims directly
   * @param sessionId ID of the session
   * @param info New claims to set
   * @returns Updated claims
   * @throws Error if session not found
   */
  public setSessionClaims(sessionId: string, info: Session): SessionClaims {
    const session = this.getSession(sessionId);
    session.claims = info;
    return session.claims;
  }

  /**
   * Updates session claims with new values
   * @param sessionId ID of the session
   * @param info Partial claims to merge
   * @returns Updated claims
   * @throws Error if session not found
   */
  public updateSessionClaims(sessionId: string, info: Partial<Session>): SessionClaims {
    const session = this.getSession(sessionId);
    session.claims = {
      ...session.claims,
      ...info,
    };
    return session.claims;
  }

  /**
   * Refreshes an existing session
   * @param sessionId ID of the session to refresh
   * @returns Updated session object
   * @throws Error if refresh fails and no credentials available
   * @throws Error if session not found
   */
  public async refreshSession(sessionId: string): Promise<Session> {
    const session = this.getSession(sessionId);
    const authProvider = this.getAuthProvider(session.authProvider);

    let refreshedSession;

    try {
      console.debug({
        message: 'Trying to refresh session',
        session: session,
      });
      refreshedSession = await authProvider.refreshSession(session);
    } catch {
      //If the auth credentials are saved, try to create a new session with those
      if (session.authCredentials) {
        console.debug({
          message: 'Trying to restart session using saved credentials',
          session: session,
        });

        refreshedSession = await authProvider.startSession(session.authCredentials);
      } else {
        throw new Error('Could not refresh or recreate the session');
      }
    }
    const discriminator = this.sessionDiscriminator(session);

    try {
      this.sessions = this.sessions.filter((s) => s.id !== discriminator);
    } catch {}

    this.sessions.push(refreshedSession);

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    return refreshedSession;
  }

  /**
   * Removes a session from storage
   * @param sessionId ID of the session to remove
   * @throws Error if session not found
   */
  public removeSession(sessionId: string): void {
    this.sessions = this.sessions.filter((s) => s.id !== sessionId);

    if (this.activeSessionId === sessionId) {
      this.activeSessionId = null;
    }

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    if (this.activeSessionId === null) {
      this.onSelectionRequired();
    }
  }

  /**
   * Gets a specific session by ID
   * @param sessionId ID of the session to retrieve
   * @returns The session object
   * @throws Error if session not found
   */
  public getSession(sessionId: string): Session {
    const session = this.sessions.find((s) => s.id === sessionId);
    if (!session) {
      throw new Error('Session not found');
    }
    return session;
  }

  /**
   * Gets an authentication provider by name
   * @param authProviderName Name of the provider
   * @returns The provider object
   * @throws Error if provider not found
   */
  public getAuthProvider(authProviderName: string): AuthProvider {
    const authProviderOrUndefined = this.authProviders.find((a) => a.name === authProviderName);
    if (!authProviderOrUndefined) {
      throw new Error(`Auth provider ${authProviderName} not found`);
    }
    return authProviderOrUndefined;
  }

  /**
   * Starts a new authentication session
   * @param providerId ID of the authentication provider
   * @param credentials User-provided credentials
   * @param options Optional configuration
   * @param options.saveCredentials Whether to save credentials for pre-filling
   * @param options.setActive Whether to activate the session
   * @returns Created session object
   * @throws Error if provider not found
   */
  public async startSession(
    providerId: string,
    credentials: GenericCredentials,
    options?: {
      saveCredentials: boolean;
      setActive: boolean;
    }
  ): Promise<Session> {
    let provider = this.getAuthProvider(providerId);

    console.debug({
      message: 'Trying to start session',
      providerId: providerId,
      credentials: credentials,
    });

    let session = await provider.startSession(credentials);

    //Clear auth credentials if saveCredentials is disabled
    if (!(options?.saveCredentials ?? true)) {
      session.authCredentials = null;
    }

    const sessionId = this.sessionDiscriminator(session);

    //Try to remove the session if it's already started
    try {
      this.sessions = this.sessions.filter((s) => s.id !== sessionId);
    } catch {}
    this.sessions.push(session);

    console.debug({
      message: 'Session started',
      session: session,
    });

    if (options?.setActive ?? true) {
      await this.setActiveSession(sessionId);
    }

    if (this.persistSessions) {
      this.persistToLocalStorage();
    }

    return session;
  }

  /**
   * Creates and initializes the sessions service
   * @param configProvider Provider for service configuration
   * @param storageProvider Provider for session storage
   * @throws Error if session discriminator is not defined
   */
  public constructor(
    private configProvider: SessionsServiceConfigProvider,
    private storageProvider: StorageProvider
  ) {
    const config = this.configProvider.getConfig();
    this.authProviders = config?.authProviders ?? [];
    this.persistSessions = config?.persistSessions ?? true;
    this.autoRefreshCredentials = config?.autoRefreshCredentials ?? true;
    this.onSelectionRequired = config?.onSelectionRequired ?? (() => {});
    this.onSignInRequired = config?.onSignInRequired ?? ((_) => {});
    this.onSessionActivated = config?.onSessionActivated ?? ((_) => {});

    this.sessionDiscriminator =
      config?.sessionDiscriminator ??
      ((_session) => {
        throw new Error('Session discriminator is not defined, please add it to the sessions service config');
      });

    if (this.persistSessions) {
      this.loadFromStorage();
    } else {
      this.clearLocalStorage();
    }
  }

  private loadFromStorage() {
    this.activeSessionId = this.storageProvider.get('active_session');
    const sessionsJson = this.storageProvider.get('sessions') ?? '[]';
    const parsedSessions = JSON.parse(sessionsJson) as Session[];
    this.sessions = parsedSessions.map(
      (ps) =>
        new Session(
          ps.id,
          ps.authProvider,
          ps.authCredentials,
          ps.refreshCredentials,
          ps.prefillInfo,
          ps.accessCredentials,
          ps.startedAt,
          ps.claims,
          ps.expiresAt,
          ps.refreshedAt,
          ps.terminatedAt
        )
    );
  }

  private persistToLocalStorage() {
    if (this.activeSessionId) {
      this.storageProvider.set('active_session', this.activeSessionId);
    } else {
      this.storageProvider.clear('active_session');
    }

    const sessionsJson = JSON.stringify(this.sessions);
    this.storageProvider.set('sessions', sessionsJson);
  }

  private clearLocalStorage() {
    this.storageProvider.clear('sessions');
    this.storageProvider.clear('active_session');
  }
}
